/**
 * Header moderno para o Dashboard Bombons&Cia
 * Fundo branco com logo otimizada e perfil do usuário
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface ModernHeaderProps {
  drawerWidth: number;
  open: boolean;
  onDrawerToggle: () => void;
}

const ModernHeader: React.FC<ModernHeaderProps> = ({ 
  drawerWidth, 
  open, 
  onDrawerToggle 
}) => {
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Gerar breadcrumbs baseado na rota atual
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbNameMap: { [key: string]: string } = {
      dashboard: 'Painel',
      sales: 'Análise de Vendas',
      stores: 'Lojas',
      inventory: 'Estoque',
      transfers: 'Transferências'
    };

    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: theme.palette.text.secondary,
          }
        }}
      >
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.text.secondary,
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main,
            }
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Dashboard
        </Link>
        {pathnames.slice(1).map((value, index) => {
          const last = index === pathnames.length - 2;
          const to = `/${pathnames.slice(0, index + 2).join('/')}`;
          
          // Verificar se é um ID de loja (número)
          const isStoreId = !isNaN(Number(value));
          let displayName = breadcrumbNameMap[value] || value;
          
          // Se for um ID de loja, obter o nome do sessionStorage
          if (isStoreId) {
            const storedStoreName = sessionStorage.getItem(`storeName_${value}`);
            if (storedStoreName) {
              displayName = storedStoreName;
            }
          }

          return last ? (
            <Typography 
              key={to} 
              color="text.primary" 
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {displayName}
            </Typography>
          ) : (
            <Link
              key={to}
              component="button"
              variant="body2"
              onClick={() => navigate(to)}
              sx={{
                color: theme.palette.text.secondary,
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
            >
              {displayName}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbNameMap: { [key: string]: string } = {
      dashboard: '',  // Não mostrar título adicional na página principal
      sales: 'Análise de Vendas',
      stores: 'Lojas',
      inventory: 'Gestão de Estoque',
      transfers: 'Rastreamento de Transferências'
    };

    if (pathnames.length === 1) return '';  // Página principal sem título extra
    
    // Se estamos em uma página de detalhes de loja
    if (pathnames[1] === 'stores' && pathnames[2] && !isNaN(Number(pathnames[2]))) {
      const storedStoreName = sessionStorage.getItem(`storeName_${pathnames[2]}`);
      return storedStoreName || `Loja ${pathnames[2]}`;
    }
    
    return breadcrumbNameMap[pathnames[1]] || '';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 3, // Garantir que o header fique sempre acima de outros elementos
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        height: { xs: '56px', sm: '64px' },
        minHeight: { xs: '56px', sm: '64px' },
        maxHeight: { xs: '56px', sm: '64px' },
        top: 0,
        left: 0,
        right: 0,
        // Header empurrado pela sidebar em desktop
        marginLeft: !isMobile && open ? `${drawerWidth}px` : 0,
        width: !isMobile && open ? `calc(100% - ${drawerWidth}px)` : '100%',
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Toolbar sx={{ 
        minHeight: { xs: '56px', sm: '64px' },
        maxHeight: { xs: '56px', sm: '64px' },
        height: { xs: '56px', sm: '64px' },
        px: { xs: 2, sm: 3 }, // Menos padding em mobile
        py: 0
      }}>
        {/* Menu button para mobile/drawer toggle */}
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          onClick={onDrawerToggle}
          edge="start"
          sx={{
            mr: { xs: 1, sm: 2 }, // Menos margin em mobile
            color: theme.palette.text.secondary,
          }}
        >
          <MenuIcon />
        </IconButton>


        {/* Título e breadcrumbs */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {getPageTitle() && !isMobile && (
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              {getPageTitle()}
            </Typography>
          )}
          
          {isMobile && (
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: '1.1rem'
              }}
            >
              Bombons&Cia BI
            </Typography>
          )}
          
          {!isMobile && (
            <Box sx={{ mt: getPageTitle() ? 0.5 : 0 }}>
              {generateBreadcrumbs()}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Status online */}
          <Chip
            label="Online"
            size="small"
            sx={{
              backgroundColor: theme.palette.success.main + '20',
              color: theme.palette.success.main,
              fontWeight: 600,
              display: { xs: 'none', md: 'flex' }
            }}
          />

          {/* Notificações */}
          <IconButton
            size="large"
            aria-label="notifications"
            color="inherit"
            onClick={handleNotificationsOpen}
            sx={{
              color: theme.palette.text.secondary,
              display: { xs: 'none', sm: 'flex' }, // Esconder em mobile muito pequeno
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <NotificationsIcon />
          </IconButton>

          {/* Profile */}
          <IconButton
            onClick={handleProfileMenuOpen}
            size="large"
            edge="end"
            color="inherit"
            sx={{
              ml: 1,
              '&:hover': {
                backgroundColor: 'transparent',
              }
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: theme.palette.primary.contrastText,
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: `0 2px 8px ${theme.palette.primary.main}30`
              }}
            >
              {authState.user?.nome?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Menu de notificações */}
        <Menu
          anchorEl={notificationsAnchor}
          open={Boolean(notificationsAnchor)}
          onClose={handleNotificationsClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 280,
              maxHeight: 400,
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notificações
            </Typography>
          </Box>
          <MenuItem onClick={handleNotificationsClose}>
            <ListItemText
              primary="Sistema atualizado"
              secondary="Novas funcionalidades disponíveis"
            />
          </MenuItem>
          <MenuItem onClick={handleNotificationsClose}>
            <ListItemText
              primary="Dados atualizados"
              secondary="BigQuery sincronizado às 14:00"
            />
          </MenuItem>
        </Menu>

        {/* Menu do perfil */}
        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 220,
            }
          }}
        >
          {/* User info */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {authState.user?.nome || 'Usuário'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {authState.user?.cargo === 'master' ? 'Diretor(a)' : (authState.user?.cargo || 'Gerente')}
            </Typography>
          </Box>

          <MenuItem onClick={handleProfileMenuClose}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Meu Perfil" />
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default ModernHeader;